
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { CashFlowT, RPPOrderT, RPPPayloadT, RPPT } from '@/core/types/rpp.types'
import { CarrierT } from '@/core/types/carriers.types'

// realisations
import { rpp } from '@/core/realisations/rpp.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useNotifications from '@/common/composable/useNotifications'

// validation
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
        'the-input': TheInput,
        'the-button': TheButton,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        rppId: {
            type: Number,
            required: true
        }
    },
    emits: ['cancel'],
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
            rpp: null as RPPT | null,
            // rpp
            company_id: null as number | null,
            cash_flow_id: null as number | null,
            act_number: null as string | null,
            act_date: null as string | null,
            pbs_date: null as string | null,
            rpp_orders: [] as RPPOrderT[],
            with_income_tax: false,
            with_nds_tax: 0 as number,
        })

        const data_state = reactive({
            cash_flows: new DataState<CashFlowT>({}),
            carriers: new DataState<CarrierT>({}),
        })

        const notifications = useNotifications()

        const EditRPPValidation = {
            cash_flow_id: { required },
            act_number: { required },
            act_date: { required },
            pbs_date: { required },
            company_id: { required },
        }

        const state_form$ = useVuelidate(EditRPPValidation, state)

        const setRPP = () => {
            if (state.rpp) {
                state.company_id = state.rpp.company_id
                state.cash_flow_id = state.rpp.cash_flow_id
                state.act_number = state.rpp.act_number
                state.act_date = state.rpp.act_date
                state.pbs_date = state.rpp.pbs_date
                state.with_nds_tax = state.rpp.with_nds_tax || 0

                if (state.rpp.income_tax && state.rpp.income_tax.id === 1) {
                    state.with_income_tax = true
                }
            }
        }

        const loadRPP = async () => {
            try {
                state.is_loading = true
                const response = await rpp.getRPP(props.rppId)

                state.rpp = response.data
                setRPP()
            } catch (error) {
                console.log(error)
            } finally {
                state.is_loading = false
            }
        }

        const editRPP = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: RPPPayloadT = {
                    id: props.rppId,
                    company_id: state.company_id,
                    cash_flow_id: state.cash_flow_id,
                    act_number: state.act_number,
                    act_date: state.act_date,
                    pbs_date: state.pbs_date,
                    income_tax_id: state.with_income_tax ? 1 : null,
                    with_nds_tax: state.with_nds_tax,
                }

                await rpp.editRPP(payload)

                notifications.notify({
                    title: `РПП успешно отредактировано`,
                    type: 'success',
                })

                emit('cancel')
            } catch (error) {
                notifications.notify({
                    title: `РПП успешно отредактировано`,
                    type: 'success',
                })

                notifications.notifyError({
                    error,
                    title: 'При редактировании РПП произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadCashFlows = async () => {
            data_state.cash_flows.pagination.current_page = 1

            try {
                data_state.cash_flows.is_loading = true
                const response = await rpp.getCashFlows()
                data_state.cash_flows.list = response.data

                data_state.cash_flows.pagination.total_pages = 1

                data_state.cash_flows.is_failed = false
                data_state.cash_flows.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cash_flows.failure.catchError(error)

                if (!data_state.cash_flows.failure.error_state.is_cancel) {
                    data_state.cash_flows.is_failed = true
                }
            } finally {
                if (!data_state.cash_flows.failure.error_state.is_cancel) {
                    data_state.cash_flows.is_loading = false
                }
            }
        }

        // companies

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const data = await carriers.getList(params)

                data_state.carriers.list = data.data
                data_state.carriers.pagination.total_pages = data.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        const onChangeTax = (value: boolean) => {
            if (!value) {
                state.with_nds_tax = 0
            }
        }

        // companies

        onBeforeMount(() => {
            loadCashFlows()
            loadRPP()
            loadCarriers(1)
        })

        const emitCancel = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        return {
            state,
            data_state,
            state_form$,
            editRPP,
            // cash flows
            loadCashFlows,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            onChangeTax,
            emitCancel,
        }
    },
})
